import React, { useEffect, useState } from 'react';

import { 
  Plane, Hotel, Car, Globe, 
  MapPin, Clock, ShieldCheck, 
  ChevronRight, Navigation 
} from 'lucide-react';
import SEO from '../seo/SEO';

const TravelTourismPage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <SEO
        title="Travel & Tourism Services | GlobalEPRC"
        description="GlobalEPRC provides reliable travel & tourism services for international and domestic (within India) travel, serving individuals, corporates and workforce mobility needs."
        path="/travel-tourism"
      />

      {/* 1. HERO SECTION */}
      <section className="pt-48 pb-24 bg-slate-950 relative overflow-hidden text-white">
        {/* Dynamic Background Images */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1436491865332-7a61a109c0f2?auto=format&fit=crop&q=80&w=2000" 
            alt="International Flight" 
            className="w-full h-full object-cover opacity-30 brightness-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-400 px-4 py-1.5 rounded-full mb-8 backdrop-blur-md border border-amber-500/20 font-bold text-xs uppercase tracking-[0.2em]">
              <Navigation className="h-4 w-4" /> Seamless Mobility
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-8 leading-tight">
              Travel & <span className="text-gold-gradient">Tourism</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-10 max-w-3xl">
              GlobalEPRC provides reliable Travel & Tourism Services for both international and within India travel, catering to individuals, corporates, and workforce mobility requirements.
            </p>
            <div className="flex flex-wrap gap-8 opacity-80">
               <div className="flex items-center gap-2"><Globe className="h-5 w-5 text-amber-500" /> International Travel</div>
               <div className="flex items-center gap-2"><MapPin className="h-5 w-5 text-amber-500" /> Domestic (Within India)</div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. CORE SERVICES GRID */}
      <section className="py-24 bg-white relative">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-gold-gradient font-bold uppercase tracking-widest text-sm mb-4">What We Offer</h2>
            <h3 className="text-4xl font-black text-slate-900 leading-tight">
              Comprehensive Travel Solutions
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                title: "Air Ticketing", 
                icon: Plane, 
                desc: "Domestic & international flight bookings with cost-effective planning.",
                img: "https://images.unsplash.com/photo-1544016768-982d1554f0b9?auto=format&fit=crop&q=80&w=400"
              },
              { 
                title: "Hotels & Resorts", 
                icon: Hotel, 
                desc: "Reliable bookings at premium hotels and resorts tailored to your needs.",
                img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&q=80&w=400"
              },
              { 
                title: "Local Transport", 
                icon: Car, 
                desc: "Efficient local transportation arrangements for seamless transit.",
                img: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=80&w=400"
              },
              { 
                title: "Coordination Support", 
                icon: ShieldCheck, 
                desc: "Dedicated travel coordination to manage every detail efficiently.",
                img: "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80&w=400"
              }
            ].map((service, i) => (
              <div key={i} className="group bg-slate-50 rounded-[2.5rem] overflow-hidden border border-slate-100 hover:shadow-2xl transition-all duration-500">
                <div className="h-48 overflow-hidden">
                  <img src={service.img} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="p-8">
                  <div className="bg-white p-4 rounded-2xl shadow-sm group-hover:bg-gold-gradient group-hover:text-white transition-all w-fit -mt-16 relative z-10 mb-6 border border-slate-100">
                    <service.icon className="h-6 w-6 text-amber-600 group-hover:text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-4">{service.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. EXPERIENCE SECTION */}
      <section className="py-24 bg-slate-50 overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-20 items-center">
            <div className="lg:w-1/2 relative">
               <div className="grid grid-cols-2 gap-4">
                  <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&q=80&w=600" className="rounded-3xl shadow-xl mt-12" alt="Travel Experience" />
                  <img src="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&q=80&w=600" className="rounded-3xl shadow-xl" alt="Destination Management" />
               </div>
               <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gold-gradient opacity-10 blur-[100px]"></div>
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-4xl font-black text-slate-900 mb-8 leading-tight">
                Travel Comfortably and <br />
                <span className="text-gold-gradient">With Confidence</span>
              </h2>
              <div className="space-y-6">
                <p className="text-lg text-slate-600 leading-relaxed">
                  Our services include air ticketing (domestic & international), hotel and resort bookings, local transportation arrangements, and travel coordination support.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  We ensure cost-effective planning, timely bookings, and seamless travel experiences. With a strong service network and customer-focused approach, we manage every travel detail efficiently.
                </p>
              </div>
              <div className="mt-10 grid sm:grid-cols-2 gap-4">
                 <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-sm">
                   <Clock className="text-amber-600 h-5 w-5" />
                   <span className="text-sm font-bold">Timely Bookings</span>
                 </div>
                 <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-sm">
                   <ShieldCheck className="text-amber-600 h-5 w-5" />
                   <span className="text-sm font-bold">Cost-Effective Planning</span>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. FINAL CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <div className="bg-slate-950 rounded-[4rem] p-16 relative overflow-hidden text-white shadow-2xl">
            <div className="absolute inset-0 bg-gold-gradient opacity-5 blur-3xl"></div>
            <h3 className="text-3xl md:text-5xl font-black mb-8 relative z-10 leading-tight">
              Ready for a Seamless <br /> Travel Experience?
            </h3>
            <button className="bg-gold-gradient text-white px-12 py-5 rounded-2xl font-bold text-lg shadow-xl hover:scale-105 transition-transform flex items-center gap-3 mx-auto relative z-10">
              Book Your Travel Now <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default TravelTourismPage;